import { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  defaultLogo?: string;
  src?: string;
  isDataLoading?: boolean; // If you supply this prop, the loader will also listen to this. If this is set to true immediately, then there is no delay
  style?: {
    width?: string | number;
    height?: string | number;
  };
  className?: string;
  delay?: number; // Want to supply a min time to show the loader.
  children?: any;
  alt?: string;
};

const standardDefault =
  'https://s3.amazonaws.com/com.givebacks.public/icons/givebacks/givebacks-96.png';

const CustomImage = ({
  defaultLogo,
  src,
  isDataLoading,
  style,
  className,
  delay,
  children,
  ...props
}: Props) => {
  const ImageElementRef = useRef<HTMLImageElement>(null);
  const [providedImage, setProvidedImage] = useState<string | undefined | null>(null);
  const [loadedSrc, setLoadedSrc] = useState('');
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const shouldDelay = useMemo(() => {
    if (isDataLoading) return true;
    return false;
  }, []);

  useEffect(() => {
    if (imageError || loadedSrc) {
      if (shouldDelay) {
        setTimeout(() => {
          setImageLoading(false);
        }, delay ?? 550);
      } else {
        setImageLoading(false);
      }
    }
  }, [imageError, loadedSrc]);

  const createTempStyles = useMemo(() => {
    const tempStyles = {
      height: style?.height ?? '0px',
      width: style?.width ?? '0px',
    };
    if (ImageElementRef.current) {
      let hasWidth = false as false | string;
      let hasHeight = false as false | string;

      if (ImageElementRef.current.style.width) hasWidth = ImageElementRef.current.style.width;
      if (ImageElementRef.current.style.height) hasHeight = ImageElementRef.current.style.height;

      // figure out what we should do to create the width for the element
      if (hasWidth) {
        tempStyles.width = hasWidth; // Attempt to set from provided style
      } else if (ImageElementRef.current.width) {
        tempStyles.width = ImageElementRef.current.width;
      } else if (hasHeight) {
        tempStyles.width = tempStyles.height; // Create a square
      } else {
        tempStyles.width = '25px';
      }

      if (hasHeight) {
        tempStyles.height = hasHeight;
      } else if (ImageElementRef.current.height) {
        tempStyles.height = ImageElementRef.current.height;
      } else if (hasWidth) {
        tempStyles.height = tempStyles.width;
      } else {
        tempStyles.height = '25px';
      }
    }
    return tempStyles;
  }, [ImageElementRef.current, style]);

  useEffect(() => {
    if (ImageElementRef?.current && providedImage) {
      const handleLoad = () => {
        setLoadedSrc(providedImage);
      };

      const handleError = () => {
        setProvidedImage(defaultLogo ?? standardDefault);
        setImageError(true);
      };

      ImageElementRef.current.addEventListener('load', handleLoad);
      ImageElementRef.current.addEventListener('error', handleError);
      ImageElementRef.current.src = providedImage;

      return () => {
        if (ImageElementRef?.current) {
          ImageElementRef.current.removeEventListener('load', handleLoad);
          ImageElementRef.current.addEventListener('error', handleError);
        }
      };
    }
  }, [ImageElementRef?.current, providedImage]);

  // When there is no image loaded and we have been told the data is loaded, set a default
  const finishedLoadingNoImage = useMemo(() => {
    if (isDataLoading === false && !!providedImage == false && !src) {
      if (!children) {
        setProvidedImage(defaultLogo ?? standardDefault);
      }
      setImageLoading(false);
      return true;
    } else {
      return false;
    }
  }, [isDataLoading, providedImage]);

  useEffect(() => {
    if (src !== providedImage) setImageLoading(true); //When the src changes, lets reset the loading state. This could happen if there was a change in data.
    setProvidedImage(src);
    // If no src is provided and isDataLoading is missing, we can go ahead and assume there is an image error somewhere!
    if (src === undefined && isDataLoading === undefined) {
      setImageError(true);
    }
  }, [src]);

  // See notes above on why this is here.
  const showLoader = useMemo(() => imageLoading || isDataLoading, [imageLoading, isDataLoading]);

  const errorShowChildren = useMemo(() => {
    if (!children) return false;
    if (imageError || finishedLoadingNoImage) {
      return true;
    }
    return false;
  }, [imageError, children, finishedLoadingNoImage]);

  const imageElementClasses = useMemo(
    () =>
      [
        (imageLoading || isDataLoading) && 'ui skeletor absolute',
        className || '',
        errorShowChildren && 'hidden',
      ].join(' '),
    [imageLoading, isDataLoading, className, errorShowChildren]
  );

  return (
    <>
      {showLoader && !errorShowChildren && (
        <span className="ui image-loader" style={createTempStyles} />
      )}
      <img ref={ImageElementRef} style={style} className={imageElementClasses} {...props} />
      {/* Show Children on ImageError */}
      {errorShowChildren && children}
    </>
  );
};

export { CustomImage as Image };
